/**
 * Germes format dvigatellari (sana + shaxs ismi) — backend bilan AYNAN bir xil.
 *
 * Backend manbalari:
 *   Germes.Core/Formatting/GermesDateFormatter.cs  + GermesMonthNames.cs
 *   DocumentService/Service/NameFormatting/GermesPersonNameFormatter.cs
 *
 * Bu fayl toza (pure) — React/Redux/localStorage'ga bog'liq emas, shuning uchun
 * ham ko'rsatishda, ham "Тизим созламалари" sahifasidagi format tekshirgichida ishlatiladi.
 */

/* ══════════════════════ Sana formati ══════════════════════
 * Tokenlar: {YYYY} {YY} {MMMM} {MMMMg} {MM} {M} {DD} {D}
 * Qavs tashqarisi literal; noma'lum token o'zgarishsiz qoladi.
 */

export const LANG_UZ_LATIN = "uz_UZ";
export const LANG_UZ_CYRILLIC = "uz_Cyrl_UZ";
export const LANG_RUSSIAN = "ru_RU";

// Oy nomlari ICU'dan EMAS — backenddagi GermesMonthNames jadvalining nusxasi.
const MONTHS_NOMINATIVE: { [lang: string]: string[] } = {
  [LANG_UZ_LATIN]: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avgust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  [LANG_UZ_CYRILLIC]: ["январ", "феврал", "март", "апрел", "май", "июн", "июл", "август", "сентябр", "октябр", "ноябр", "декабр"],
  [LANG_RUSSIAN]: ["январь", "февраль", "март", "апрель", "май", "июнь", "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"],
};

// Rus tilida sana kontekstida oy qaratqich kelishigida keladi: "16 декабря".
const MONTHS_GENITIVE: { [lang: string]: string[] } = {
  [LANG_RUSSIAN]: ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],
};

const monthName = (language: string, month: number, genitive: boolean): string => {
  const lang = MONTHS_NOMINATIVE[language] ? language : LANG_RUSSIAN;
  if (genitive && MONTHS_GENITIVE[lang]) return MONTHS_GENITIVE[lang][month - 1];
  return MONTHS_NOMINATIVE[lang][month - 1];
};

const pad2 = (value: number): string => (value < 10 ? `0${value}` : String(value));

/** Har bir til uchun standart sana patterni (backend GetDefaultPattern bilan bir xil). */
export const getDefaultDatePattern = (language: string): string => {
  switch (language) {
    case LANG_UZ_LATIN:
      return "{YYYY} yil {DD} {MMMM}";
    case LANG_UZ_CYRILLIC:
      return "{YYYY} йил {DD} {MMMM}";
    default:
      return "{YYYY} год {DD} {MMMMg}";
  }
};

/** Sanani token-pattern bo'yicha formatlaydi. Pattern bo'sh bo'lsa — shu til uchun standart. */
export const formatGermesDate = (date: Date, pattern: string, language: string): string => {
  const usedPattern = pattern && pattern.trim() ? pattern : getDefaultDatePattern(language);
  return usedPattern.replace(/\{([A-Za-z]+)\}/g, (token: string, name: string) => {
    switch (name) {
      case "YYYY":
        return String(date.getFullYear());
      case "YY":
        return pad2(date.getFullYear() % 100);
      case "MMMM":
        return monthName(language, date.getMonth() + 1, false);
      case "MMMMg":
        return monthName(language, date.getMonth() + 1, true);
      case "MM":
        return pad2(date.getMonth() + 1);
      case "M":
        return String(date.getMonth() + 1);
      case "DD":
        return pad2(date.getDate());
      case "D":
        return String(date.getDate());
      default:
        return token; // noma'lum token -> literal (xavfsiz)
    }
  });
};

/**
 * EnvironmentService `document_regdate_format` qiymatidan
 * ({"ru_RU":"...","uz_UZ":"...","uz_Cyrl_UZ":"..."}) til uchun patternni ajratadi.
 */
export const resolveDatePatternFromEnv = (envValue?: null | string, language?: string): string => {
  if (!envValue || !language) return "";
  try {
    const map = JSON.parse(envValue);
    if (!map || typeof map !== "object" || Array.isArray(map)) return "";
    const pattern = map[language];
    return typeof pattern === "string" ? pattern : "";
  } catch {
    return "";
  }
};

/* ══════════════════════ Shaxs ismi formati ══════════════════════
 * Tokenlar: {L} {F} {O} {Li} {Fi} {Oi} {LU} {FU} {OU}
 */

const PERSON_NAME_DIGRAPHS = ["ch", "sh", "ya", "ye", "yo", "yu"];

/** Format turi (rol) — front `personNameFormation(obj, type)` va backend `type` raqamlari bilan bir xil. */
export const PERSON_NAME_ROLES = ["1", "2", "3", "4"];

export const PERSON_NAME_DEFAULT_PATTERNS: { [role: string]: string } = {
  1: "{L} {Fi} {Oi}", // Kurbanov M. M.
  2: "{F} {Li} {Oi}", // Muzaffar K. M.
  3: "{L} {F} {O}", // Kurbanov Muzaffar Mahmudovich
  4: "{Fi}{Li}{Oi}", // M.K.M.
};

export const getDefaultPersonNamePattern = (role: string): string => PERSON_NAME_DEFAULT_PATTERNS[role] || PERSON_NAME_DEFAULT_PATTERNS[1];

/** Ism bosh harfi + nuqta. Digraf (Sh/Ch/Ya/Ye/Yo/Yu) ikki harf bilan olinadi. */
export const personNameInitial = (name?: null | string): string => {
  const trimmed = (name || "").trim();
  if (!trimmed) return "";
  if (trimmed.length >= 2 && PERSON_NAME_DIGRAPHS.includes(trimmed.substring(0, 2).toLowerCase())) {
    return `${trimmed.charAt(0).toUpperCase()}${trimmed.charAt(1)}.`;
  }
  return `${trimmed.charAt(0).toUpperCase()}.`;
};

/** Ism qismlarini token-pattern bo'yicha formatlaydi (bo'sh qismlardan qolgan probellar tozalanadi). */
export const applyPersonNamePattern = (firstName: string, lastName: string, fatherName: string, pattern: string): string =>
  (pattern || getDefaultPersonNamePattern("1"))
    .replace(/\{([A-Za-z]+)\}/g, (token: string, name: string) => {
      switch (name) {
        case "L":
          return lastName;
        case "F":
          return firstName;
        case "O":
          return fatherName;
        case "Li":
          return personNameInitial(lastName);
        case "Fi":
          return personNameInitial(firstName);
        case "Oi":
          return personNameInitial(fatherName);
        case "LU":
          return lastName.toUpperCase();
        case "FU":
          return firstName.toUpperCase();
        case "OU":
          return fatherName.toUpperCase();
        default:
          return token; // noma'lum token -> literal (xavfsiz)
      }
    })
    .replace(/\s{2,}/g, " ")
    .trim();

/**
 * EnvironmentService `person_name_format` qiymatidan ({"1":"...","2":"..."}) rol uchun patternni ajratadi.
 */
export const resolvePersonNamePatternFromEnv = (envValue?: null | string, role?: string): string => {
  if (!envValue || !role) return "";
  try {
    const map = JSON.parse(envValue);
    if (!map || typeof map !== "object" || Array.isArray(map)) return "";
    const pattern = map[role];
    return typeof pattern === "string" ? pattern : "";
  } catch {
    return "";
  }
};
